import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';
import { coreApi } from '$api-clients';
import { asApiError } from '$api-clients/asApiError';
import { toMlsSearchResult } from './toMlsSearchResult';
import { type MlsSearchOutcome, MlsSearchStatus } from './types';

export const useMlsSearch = () =>
  useCallback(
    async (
      vendor: string,
      mlsNumber: string,
      compareState?: string,
    ): Promise<MlsSearchOutcome> => {
      const { data, error: searchError } = await coreApi.GET(
        '/mls-services/{mlsVendor}/{mlsNumber}',
        {
          params: {
            path: { mlsVendor: vendor, mlsNumber },
            ...(compareState && { query: { compareState } }),
          },
        },
      );

      if (searchError !== undefined) {
        const apiError = asApiError(searchError);

        if (apiError?.code === 'MLS_INVALID_STATE') {
          return { status: MlsSearchStatus.InvalidState };
        }

        // A listing the vendor has no record of is the ordinary miss; anything
        // else is an outage worth seeing, and all of them read as "not found".
        // Matched on the status too, so a 404 that loses its body on the way
        // through the proxy does not read as an outage.
        const isOrdinaryMiss =
          apiError?.code === 'RESOURCE_NOT_FOUND' ||
          apiError?.statusCode === 404;

        if (!isOrdinaryMiss) {
          datadogRum.addError('MLS search failed', {
            vendor,
            mlsNumber,
            compareState,
            apiError,
          });
        }

        return { status: MlsSearchStatus.NotFound };
      }

      return { status: MlsSearchStatus.Found, result: toMlsSearchResult(data) };
    },
    [],
  );
